import { SxProps, Theme } from '@mui/material';
import { sharedCardStyles } from '../SharedCard.styles';
import { ENDED_PROGRESS_GRAY } from '../../../../styles/GlobalStyles';

export const experienceCardStyles = {
  cardFrame: (theme: Theme, isEndedExperience: boolean = false, canOpenExperience: boolean = true) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      cursor: canOpenExperience ? 'pointer' : 'default',
      '&::before': {
        content: '""',
        width: '9px',
        flex: '0 0 9px',
        backgroundColor: 'transparent',
        borderTopLeftRadius: '3px',
        borderBottomLeftRadius: '3px',
        transition: 'background-color 0.2s ease',
        marginRight: '-1px',
        position: 'relative',
        zIndex: 1,
      },
      '&::after': {
        content: '""',
        width: '9px',
        flex: '0 0 9px',
        backgroundColor: 'transparent',
        borderTopRightRadius: '3px',
        borderBottomRightRadius: '3px',
        transition: 'background-color 0.2s ease',
        marginLeft: '-1px',
        position: 'relative',
        zIndex: 1,
      },
      '&:hover::before, &:hover::after': {
        backgroundColor: isEndedExperience ? ENDED_PROGRESS_GRAY : theme.palette.primary.main,
      },
    }) as SxProps<Theme>,

  card: (theme: Theme, canOpenExperience: boolean = true) =>
    ({
      ...sharedCardStyles.card(theme, {
        direction: 'column',
        cursor: canOpenExperience ? 'pointer' : 'default',
      }),
      width: 'calc(100% - 16px)',
      border: 'none',
      borderRadius: 0,
      boxShadow: '3px 3px 6px rgba(0, 0, 0, 0.35)',
      overflow: 'hidden',
      position: 'relative',
      zIndex: 0,
      '&:hover': {
        borderColor: 'transparent',
      },
      '&:hover .experience-card-title': canOpenExperience ? {
        textDecoration: 'underline',
      } : undefined,
      '&:hover .end-now-link': {
        opacity: 1,
        visibility: 'visible',
      },
      '&:hover .reactivate-link': {
        opacity: 1,
        visibility: 'visible',
      },
    }) as SxProps<Theme>,

  topRow: () =>
    ({
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      position: 'relative',
    }) as SxProps<Theme>,

  topActionsContainer: (theme: Theme) =>
    ({
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    }) as SxProps<Theme>,

  studentProgressCountContainer: () =>
    ({
      display: 'flex',
      alignItems: 'center',
    }) as SxProps<Theme>,

  endNowLink: (theme: Theme) =>
    ({
      ...sharedCardStyles.actionLink(theme),
    }) as SxProps<Theme>,

  endNowIcon: () =>
    ({
      width: '18px',
      height: '18px',
      marginRight: '4px',
      display: 'block',
    }) as SxProps<Theme>,

  imageContainer: (theme: Theme) =>
    ({
      ...sharedCardStyles.imageContainer(theme),
    }) as SxProps<Theme>,

  image: () =>
    ({
      ...sharedCardStyles.image(),
    }) as SxProps<Theme>,

  contentContainer: (theme: Theme) =>
    ({
      ...sharedCardStyles.contentContainer(theme, theme.spacing(1, 8, 1, 1.25)),
    }) as SxProps<Theme>,

  title: (theme: Theme, canOpenExperience: boolean = true) =>
    ({
      ...sharedCardStyles.title(theme, canOpenExperience),
      '&:focus-visible': canOpenExperience ? {
        outline: `2px solid ${theme.palette.primary.dark}`,
        outlineOffset: '2px',
      } : undefined,
    }) as SxProps<Theme>,

  description: (theme: Theme, maxLines: number = 2) => {
    return ({
      ...sharedCardStyles.description(theme, maxLines),
    }) as SxProps<Theme>;
  },

  metadataRowContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginTop: 'auto',
      minHeight: '28px',
      width: '100%',
    }) as SxProps<Theme>,

  rightMetadataContainer: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'flex-start',
      marginLeft: 'auto',
      flexShrink: 0,
      marginRight: `-${theme.spacing(8)}`,
      paddingRight: theme.spacing(1),
    }) as SxProps<Theme>,

  standardsContainer: (theme: Theme) =>
    ({
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
      paddingTop: theme.spacing(0.25),
    }) as SxProps<Theme>,

  badgesRowContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      flexWrap: 'nowrap',
      flexShrink: 0,
      gap: theme.spacing(1),
    }) as SxProps<Theme>,

  badge: (theme: Theme) =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '28px',
      boxSizing: 'border-box',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      padding: theme.spacing(0, 1.5),
      borderRadius: '3px',
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,

  endedScoresContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      flexWrap: 'nowrap',
      gap: theme.spacing(1),
      marginBottom: 0,
    }) as SxProps<Theme>,

  endedScoreItem: () =>
    ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }) as SxProps<Theme>,

  endedScoreBadge: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(0.25, 0.75),
      minHeight: '18px',
      minWidth: '45px',
      boxSizing: 'border-box',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '3px',
    }) as SxProps<Theme>,

  endedScoreTitle: (theme: Theme) =>
    ({
      fontSize: '9px',
      color: theme.palette.text.secondary,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      marginBottom: 0,
    }) as SxProps<Theme>,

  endedScoreValue: (theme: Theme) =>
    ({
      fontSize: theme.typography.caption.fontSize,
      color: theme.palette.primary.contrastText,
      fontWeight: theme.typography.fontWeightMedium,
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,

  studentProgressContainer: (theme: Theme) =>
    ({
      margin: 0,
      padding: 0,
      width: '100%',
      alignSelf: 'stretch',
      boxSizing: 'border-box',
      paddingRight: 0,
    }) as SxProps<Theme>,

  endNowConfirmOverlay: () =>
    ({
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(64, 64, 64, 0.9)',
      padding: '16px',
      textAlign: 'center',
    }) as SxProps<Theme>,

  endNowConfirmPanel: () =>
    ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '75%',
      maxWidth: '75%',
    }) as SxProps<Theme>,

  endNowConfirmTitle: () =>
    ({
      color: '#FFFFFF',
      fontSize: '20px',
      fontWeight: 400,
      marginBottom: '16px',
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,

  reactivateConfirmTitle: () =>
    ({
      color: '#FFFFFF',
      fontSize: '16px',
      fontWeight: 400,
      marginBottom: '16px',
      lineHeight: 1.4,
      whiteSpace: 'normal',
      textAlign: 'center',
    }) as SxProps<Theme>,

  endNowConfirmButtons: () =>
    ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
    }) as SxProps<Theme>,
};

export default experienceCardStyles;
